import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject, Provider } from 'mobx-react';
import { differenceInDays } from 'date-fns';
import { isInitiativeSlug } from 'utils';

import { analytics } from 'core/analytics';

// i18n
import { I18nextProvider } from 'react-i18next';
import { i18n } from 'config/i18n';
// views
import { configure_bloqi } from 'coreConf';
import { ClientContext } from 'apps/client/index';

// Global app styles
import { ThemeProvider } from 'styled-components';
import globalStyles from 'styles/globalStyles';
import GlobalStylesMobile from 'styles/globalStylesMobile';
import theme from 'styles/themes/wikifactory';
// Chakra
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import chakraTheme from 'styles/themes/wikifactory/chakra';
import { muiTheme } from 'styles/themes/wikifactory/mui';
import { Global } from '@emotion/react';

// routing
import { routeNode } from 'core/routing';

// Context providers
import { RootStoresProvider } from 'apps/hooks';

// whitelabel
import { whitelabelPatchTheme } from 'core/whitelabel';

// components
import DocumentHead from 'legacyComponents/documentHead';
import { ProgressBar } from 'legacyComponents/progressBar';
import { PolicyMessage } from 'apps/components/policyMessage';
import { AlertCenter, Button } from 'components';

// apps
import { FourOhFour } from 'apps/fourOhFour/index';
import { getSystemCondition } from 'apps/routes/utils';
import { MessageBanner } from 'legacyComponents/messageBanner';
import { ModalContainer } from 'apps/modals';
import { ProcessMonitor } from 'apps/processMonitor';
import Footer from 'apps/components/footer/footer';

// top-level stores
import { rootStore } from 'apps/store';

// routers
import { HomeRouter } from 'apps/home/router';
import { ProfileRouter } from 'apps/profile/router';
import { InitiativeRouter } from 'apps/initiative/router';
import WorkspaceRouter from 'apps/workspace/router';

import { ChannelRouter } from 'apps/channel/router';
// import { ManufacturingRouter } from 'apps/manufacturing/router';

import RFQPage from 'apps/rfq/rfqPage';
import RFQsPage from 'apps/rfq/rfqList/page';
import { QuotePage } from 'apps/shipmentQuote/QuotePage';
import { OrderPage } from 'apps/order/OrderPage';
import { ViewerPage } from 'apps/viewer';
import { initYellowBanner } from 'apps/components/header/blocks/yellowBanner';
import { conf as globalConf } from 'apps/config.yaml';

import { ChatControlProvider } from 'libs/rendering/hooks';

import { ViewerControlProvider } from 'libs/rendering/gltf/hooks';

import { ChatControl } from 'libs/rendering/chatControl';
import { ViewerControl } from 'libs/rendering/gltf/viewerControl';
import { Canvas3D } from 'libs/rendering/gltf/components/canvas3D';

import TagManager from 'react-gtm-module';
import FrameMobile from 'apps/components/FrameMobile';
import Frame from 'apps/components/Frame';
import SpacingToAvoidFrame from 'apps/components/Frame/SpacingToAvoidFrame';

import { frameStore } from 'apps/components/Frame/appManager';

import { VoxelMuiThemeProvider } from '@wikifactory/voxel-ui';

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { isTrialExpired } from 'utils/freeTrials';

import { createNotification, clearNotificationsInIndexDB } from './utils';

import { CreateShipmentQuotePage } from './shipmentQuote/CreateShipmentQuotePage';
import { OnboardingRouter } from './onboarding/router';

import TestPage from '../../tests/sandbox';
import '../tailwind.css';
import { FrameSidebar } from './components/Frame/FrameSidebar';
import { SubHeader } from './components/Frame/FrameHeader';
import colors from '../styles/themes/wikifactoryNew/styles/colors';
import { FrameSidebarWidth } from '../styles/constants';

// every time refresh page, will clear notifications in index db
clearNotificationsInIndexDB();

const desktopMode = isInDesktopMode();

// patch fetch to return a Promise with 'finally'
window._fetch = fetch;
if (!window._fetch) {
  window.fetch = (...args) => Promise.resolve(window._fetch(...args));
}

if (!window.createNotification) {
  window.createNotification = createNotification;
}

// polyfill canvas.toBlob
if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
    value(callback, type, quality) {
      const canvas = this;
      setTimeout(() => {
        const binStr = atob(canvas.toDataURL(type, quality).split(',')[1]);
        const len = binStr.length;
        const arr = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
          arr[i] = binStr.charCodeAt(i);
        }

        callback(new Blob([arr], { type: type || 'image/png' }));
      });
    },
  });
}

configure_bloqi();
initYellowBanner();

const tagManagerArgs = {
  gtmId: GOOGLE_TAGS_ID,
};

const components = {
  home: HomeRouter,
  // dashboard: HomeRouter,
  profile: ProfileRouter,
  initiative: InitiativeRouter,
  channel: ChannelRouter,

  // manufacturing: ManufacturingRouter,
  rfq: RFQPage,
  rfqs: RFQsPage,
  quote: QuotePage,
  order: OrderPage,
  viewer: ViewerPage,

  workspace: WorkspaceRouter,
  'new-quote': CreateShipmentQuotePage,
  // /posts/id" PostPage
  test: TestPage,
  onboarding: OnboardingRouter,
};

@inject('routerStore', 'clientStore', 'headerStore')
@observer
class App extends React.Component {
  constructor(props) {
    super(props);
    props.clientStore.initialize();
    frameStore.initialize();
    analytics.initialize(props.clientStore.signedInProfile, props.routerStore);
  }

  componentDidMount() {
    const { routerStore, clientStore, headerStore } = this.props;

    clientStore.QuerySpaceForPlan(clientStore.slugFromRouteOrSpace);

    if (GOOGLE_TAGS_ID) {
      TagManager.initialize(tagManagerArgs);
    }

    // // Add Hubspot script in production and staging
    // if (window.location.hostname.includes('wikifactory.com')) {
    //   const script = document.createElement('script');
    //   script.type = 'text/javascript';
    //   script.id = 'hs-script-loader';
    //   script.async = true;
    //   script.defer = true;
    //   script.src = '//js-eu1.hs-scripts.com/26723949.js';

    //   // For some reason this line is required
    //   script.onload = () => {};
    //   document.body.appendChild(script);
    // }

    // Add user guiding script in production only
    if (window.location.origin === 'https://wikifactory.com') {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'userguiding-script-loader';
      script.async = true;
      script.defer = true;
      script.innerHTML = `
          (function(g, u, i, d, e, s) {
              g[e] = g[e] || [];
              var f = u.getElementsByTagName(i)[0];
              var k = u.createElement(i);
              k.async = true;
              k.src = 'https://static.userguiding.com/media/user-guiding-' + s + '-embedded.js';
              f.parentNode.insertBefore(k, f);
  
              if (g[d]) return;
  
              var ug = g[d] = { q: [] };
  
              ug.c = function(n) {
                  return function() {
                      ug.q.push([n, arguments]);
                  };
              };
  
              var m = ['previewGuide', 'finishPreview', 'track', 'identify', 'hideChecklist', 'launchChecklist'];
  
              for (var j = 0; j < m.length; j += 1) {
                  ug[m[j]] = ug.c(m[j]);
              }
          })(window, document, 'script', 'userGuiding', 'userGuidingLayer', '1IX87085YBPID');
      `;
      document.body.appendChild(script);
    }

    document.addEventListener('touchstart', () => {}, true);

    if (desktopMode) {
      document.addEventListener(
        'click',
        event => {
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
          }
        },
        true,
      );
    }

    // hide mobile nav on navigate success.
    const { router } = routerStore;
    const hideMobileNav = () => ({
      onTransitionSuccess: () => {
        if (headerStore.hamburgerActive) {
          headerStore.ToggleHamburger();
        }
      },
    });
    router.usePlugin(hideMobileNav);
  }

  render() {
    const { route, navigate } = this.props.routerStore;
    if (!route) return null;
    const { clientStore } = this.props;
    const { hamburgerActive } = this.props.headerStore;
    let segment;
    if (['INTERMEDIATOR', 'MANUFACTURER'].includes(clientStore.currentPortal)) {
      if (route.name.startsWith('workspace')) {
        // delegate handling of intermediator to WorkspaceRouter
        segment = 'workspace';
      } else {
        [, segment] = route.name.split('.');
      }
    } else {
      [segment] = route.name.split('.');
    }

    const View = components[segment] || FourOhFour;
    const title = globalConf.siteTitle;
    const description = globalConf.siteDescription;
    const { trialPeriodEnd, creditCards, billingSubscriptions } =
      clientStore.currentSpaceData || {};
    const { canDeleteIn, invoices } = clientStore.currentSpaceData || {};
    const isInitiativeSpace = isInitiativeSlug(
      clientStore.slugFromRouteOrSpace,
    );

    const isPlanCanceled = billingSubscriptions
      ? billingSubscriptions[billingSubscriptions.length - 1]?.cancelAtPeriodEnd
      : false;
    const billingStatus =
      billingSubscriptions?.[billingSubscriptions?.length - 1]?.status;
    const isTrialActive = trialPeriodEnd && !isTrialExpired(trialPeriodEnd);
    const hasCreditCards = creditCards?.length;

    const setToMidnight = date => {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    };

    const remainDays = trialPeriodEnd
      ? `${differenceInDays(
          setToMidnight(new Date(trialPeriodEnd)),
          setToMidnight(new Date()),
        )}`
      : null;
    const isMobileViewport = clientStore.isMobile;

    const freeTrialTipRoutes = [
      'workspace.spaceProjects',
      'workspace.spaceFiles',
      'workspace.sharedWithFiles',
      'workspace.sharedWithProjects',
      'workspace.rfqs',
      'workspace.quotes',
      'workspace.orders',
    ];

    const shouldShowFreeTrialTip =
      isTrialActive &&
      remainDays &&
      !isPlanCanceled &&
      !hasCreditCards &&
      freeTrialTipRoutes.includes(route.name) &&
      !isMobileViewport;

    const showShowPaymentTip =
      isInitiativeSpace &&
      isTrialExpired(trialPeriodEnd) &&
      !hasCreditCards &&
      canDeleteIn &&
      freeTrialTipRoutes.includes(route.name) &&
      !isMobileViewport &&
      billingStatus !== 'CANCELED';

    const latestInvoice = invoices?.[0];

    const {
      isLoggedIn,
      shouldShowFrame,
      shouldHideSubHeader,
      shouldHideLeftMenusOnMobile,
      shouldShowFrameSideBar,
      shouldGotoConfirmProfilePage,
    } = getSystemCondition();

    if (shouldGotoConfirmProfilePage) {
      navigate('onboarding.confirmProfile');
    }

    const mainContent = (
      <>
        <ProgressBar />
        <MessageBanner />
        <ModalContainer />
        <View />
        <AlertCenter />
        {
          // - in the preview generation I used to manually delete the
          // PolicyMessage(cookie banner), but that was unreliable so instead
          // I prevent it from appearing here
          segment !== 'viewer' && <PolicyMessage />
        }
        <ProcessMonitor />
      </>
    );

    const expireBgColor =
      Number(remainDays) <= 7 ? 'bg-wfYellow800' : 'bg-wfBlue';
    const expireTextColor = Number(remainDays) <= 7 ? 'wfBlack' : 'wfBlack';
    const textColor = Number(remainDays) <= 7 ? 'text-black' : 'text-white';

    const freeTrialTip = (
      <>
        <div
          style={{ width: `calc(100% - ${FrameSidebarWidth}px)` }}
          className={`h-12 ${expireBgColor} z-50 fixed top-16 flex justify-center items-center ${textColor}`}
        >
          Your trial expires in
          <span className='font-bold inline-block ml-1'>{remainDays} days</span>
          . To avoid losing access
          <Button
            key='markAllReadButton'
            text='Add Payment Method'
            size='small'
            variant='primary'
            style={{
              height: 26,
              padding: '5px 11px 2px',
              marginLeft: 8,
              border: 'none',
              color: colors[expireTextColor],
            }}
            onClick={() => {
              clientStore.routerStore.navigate(
                'workspace.spaceSettings.billing',
                {
                  spacePath: route.params.spacePath,
                },
              );
            }}
            outline
          />
        </div>
        <div className='h-10' />
      </>
    );

    const AddPaymentTip = (
      <>
        <div
          style={{ width: `calc(100% - ${FrameSidebarWidth}px)` }}
          className='h-12 bg-red-500 z-50 fixed top-16 flex justify-center items-center text-white'
        >
          {latestInvoice?.status === 'OPEN'
            ? 'Payment failed. We’re retrying the charge. To avoid another failed attempt. Please'
            : 'Your free trial has ended. To prevent your subscription from being canceled. Please'}
          <Button
            key='markAllReadButton'
            text='Add Payment Method'
            size='small'
            variant='primary'
            style={{
              height: 26,
              padding: '5px 11px 2px',
              marginLeft: 8,
              border: 'none',
              color: colors[expireTextColor],
            }}
            onClick={() => {
              clientStore.routerStore.navigate(
                'workspace.spaceSettings.billing',
                {
                  spacePath: route.params.spacePath,
                },
              );
            }}
            outline
          />
        </div>
        <div className='h-10' />
      </>
    );

    // This is a hack to way, since sub header is fixed, and will cause other layout height collapse
    const HackElementForSubHeader = () => {
      return <div className='h-8 w-full' />;
    };

    return (
      <>
        {hamburgerActive && <GlobalStylesMobile />}
        <DocumentHead title={title} description={description} />
        <main>
          {shouldShowFrame && !isMobileViewport && (
            <Frame shouldShowFrameSideBar={shouldShowFrameSideBar} />
          )}
          {shouldShowFrame && isMobileViewport && (
            <FrameMobile
              shouldHideLeftMenusOnMobile={shouldHideLeftMenusOnMobile}
            />
          )}
          <div className={shouldShowFrameSideBar ? 'flex w-full' : ''}>
            {shouldShowFrameSideBar && <FrameSidebar />}
            <SpacingToAvoidFrame
              active={shouldShowFrame}
              desktopMode={desktopMode}
              withoutLeftSpacing={!shouldShowFrameSideBar}
            >
              {!shouldHideSubHeader && <SubHeader />}
              {shouldShowFreeTrialTip && freeTrialTip}
              {showShowPaymentTip && AddPaymentTip}
              {!shouldHideSubHeader && <HackElementForSubHeader />}
              {mainContent}
            </SpacingToAvoidFrame>
          </div>
        </main>
        {!isLoggedIn && <Footer />}
      </>
    );
  }
}

@observer
export class Wikifactory extends React.Component {
  constructor(props) {
    super(props);
    this.chatControl = new ChatControl();
    this.viewerControl = new ViewerControl();
  }

  render() {
    const patchedTheme = whitelabelPatchTheme(theme);

    // TODO: remove 'Provider' when we've removed all mobx injects
    return (
      <I18nextProvider i18n={i18n}>
        <ClientContext>
          <ThemeProvider theme={patchedTheme}>
            <MuiThemeProvider theme={muiTheme}>
              <VoxelMuiThemeProvider theme={muiTheme}>
                <ChakraProvider theme={chakraTheme}>
                  <Global styles={globalStyles} />
                  <ColorModeScript
                    initialColorMode={chakraTheme?.config?.initialColorMode}
                  />
                  <RootStoresProvider value={rootStore}>
                    <Provider rootStore={rootStore} {...rootStore.stores}>
                      <ChatControlProvider value={this.chatControl}>
                        <ViewerControlProvider value={this.viewerControl}>
                          <RouteApp />
                          <Canvas3D />
                        </ViewerControlProvider>
                      </ChatControlProvider>
                    </Provider>
                  </RootStoresProvider>
                </ChakraProvider>
              </VoxelMuiThemeProvider>
            </MuiThemeProvider>
          </ThemeProvider>
        </ClientContext>
      </I18nextProvider>
    );
  }
}

export const RouteApp = routeNode('')(App);

App.propTypes = {
  routerStore: PropTypes.object,
  clientStore: PropTypes.object,
  headerStore: PropTypes.object,
};
